import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav from './LandingNav';
import Footer from './Footer';

const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      image: '/images/all/multi-wallet-connection.png',
      title: 'Connect or Create Wallet',
      description: 'Start by connecting your existing wallet (MetaMask, Trust Wallet, etc.) or create a new wallet with us. No email or password required - pure Web3 authentication.',
      details: [
        'Support for 100+ wallet types',
        'Secure WalletConnect integration',
        'Create new wallet with recovery phrase',
        'Instant authentication via wallet signature'
      ],
      cta: 'Connect Wallet'
    },
    {
      number: '02',
      image: '/images/all/ai-protection-visual.png',
      title: 'Enable AI Protection',
      description: 'Our AI system immediately starts monitoring your wallet for threats, analyzing transaction patterns, and protecting against unauthorized access attempts.',
      details: [
        'Real-time threat detection',
        'Behavioral analysis',
        'Automated security alerts',
        'Multi-chain monitoring'
      ],
      cta: 'Activate Protection'
    },
    {
      number: '03',
      image: '/images/all/backup-system-illustration.png',
      title: 'Backup Your Assets',
      description: 'Securely backup your recovery phrase and wallet data across multiple encrypted locations. Never lose access to your crypto assets.',
      details: [
        'Encrypted cloud backup',
        'Multi-location redundancy',
        'Automated backup scheduling',
        'Secure recovery phrase storage'
      ],
      cta: 'Setup Backup'
    },
    {
      number: '04',
      image: '/images/all/transaction-monitor.png',
      title: 'Monitor & Manage',
      description: 'Track all your transactions, receive real-time alerts, and manage your assets across multiple chains from one secure dashboard.',
      details: [
        'Cross-chain transaction tracking',
        'Real-time notifications',
        'Portfolio analytics',
        'Gas fee optimization'
      ],
      cta: 'View Dashboard'
    }
  ];

  const walletOptions = [
    {
      icon: '🔗',
      title: 'Connect Existing Wallet',
      description: 'Already have a wallet? Connect it instantly using WalletConnect or browser extension.',
      action: 'Connect Now'
    },
    {
      icon: '✨',
      title: 'Create New Wallet',
      description: 'New to crypto? We\'ll generate a secure wallet with a recovery phrase for you.',
      action: 'Create Wallet'
    },
    {
      icon: '🔐',
      title: 'Import Recovery Phrase',
      description: 'Have a recovery phrase? Import your existing wallet securely.',
      action: 'Import Wallet'
    }
  ];

  const securitySteps = [
    {
      icon: '🔑',
      title: 'Your Keys, Your Control',
      description: 'Private keys are encrypted and stored only on your device. We never have access to your funds.'
    },
    {
      icon: '🛡️',
      title: 'Zero-Knowledge Authentication',
      description: 'Authentication happens via wallet signatures. No passwords, no personal data stored.'
    },
    {
      icon: '🔒',
      title: 'End-to-End Encryption',
      description: 'All backups and recovery data are encrypted with military-grade AES-256 encryption.'
    },
    {
      icon: '⚡',
      title: 'Instant Recovery',
      description: 'Lost your device? Recover your wallet instantly using your recovery phrase.'
    }
  ];

  return (
    <div className="min-h-screen bg-deepspace">
      <LandingNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-purple/10 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mb-6">
            How
            <span className="block bg-gradient-to-r from-cyber-blue to-cyber-purple bg-clip-text text-transparent">
              Trust Vault LLC Works
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Secure your crypto assets in 4 simple steps. No email, no password - just pure Web3 security.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-32">
          {steps.map((step, index) => (
            <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
              <div className={`relative ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="absolute -top-8 -left-8 text-8xl font-bold text-cyber-blue/10">{step.number}</div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 rounded-3xl blur-2xl"></div>
                  <div className="relative bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-3xl p-8">
                    <img src={step.image} alt={step.title} className="w-full h-auto object-contain" />
                  </div>
                </div>
              </div>

              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                <div className="inline-block px-4 py-2 bg-cyber-blue/10 border border-cyber-blue/30 rounded-full text-cyber-blue font-semibold text-sm">
                  Step {step.number}
                </div>
                <h3 className="text-3xl lg:text-4xl font-bold text-text-primary">{step.title}</h3>
                <p className="text-lg text-text-secondary leading-relaxed">{step.description}</p>
                
                <div className="space-y-3">
                  {step.details.map((detail, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-cyber-blue rounded-full"></div>
                      <span className="text-text-primary">{detail}</span>
                    </div>
                  ))}
                </div>

                <button className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-semibold rounded-xl hover:scale-105 transition-all duration-300">
                  {step.cta}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Wallet Options */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Choose Your Entry Point
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Multiple ways to get started with Trust Vault LLC - all equally secure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {walletOptions.map((option, index) => (
              <div key={index} className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-8 text-center hover:border-cyber-blue/50 transition-all duration-300 hover:scale-105 shadow-[0_8px_30px_rgb(0,0,0,0.5)]">
                <div className="text-5xl mb-6">{option.icon}</div>
                <h3 className="text-2xl font-bold text-text-primary mb-4">{option.title}</h3>
                <p className="text-text-secondary mb-6 leading-relaxed">{option.description}</p>
                <button className="w-full py-3 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-semibold rounded-xl hover:scale-105 transition-transform duration-300">
                  {option.action}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Security First, Always
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Every step is designed with your security and privacy in mind.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securitySteps.map((step, index) => (
              <div key={index} className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-6 text-center hover:border-cyber-green/50 transition-all duration-300">
                <div className="text-4xl mb-4">{step.icon}</div>
                <h3 className="text-lg font-bold text-text-primary mb-3">{step.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-cyber-blue/10 via-cyber-purple/10 to-cyber-green/10 backdrop-blur-sm border border-cyber-blue/30 rounded-3xl p-12 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Join thousands of users securing their crypto assets with Trust Vault LLC.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth" className="px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300 inline-block text-center">
                Connect Wallet
              </Link>
              <Link to="/auth" className="px-8 py-4 border border-cyber-blue/50 text-cyber-blue font-bold rounded-xl backdrop-blur-sm hover:bg-cyber-blue/10 transition-all duration-300 inline-block text-center">
                Create Wallet
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorks;